import { type FC, useMemo, useState } from 'react'

import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import user_placeholder from 'assets/images/icons/user_placeholder.svg'
import * as spriteIcons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import { UserNotes } from 'components-ref/organisms'
import { useModalContext } from 'context/modalContext'
import { getLink } from 'helpers/appLinks'
import { addFanToGroups } from 'services/endpoints/fanGroup'
import { getFanDetails } from 'services/endpoints/profile'
import { useUserProfile } from 'store'
import { useFanDetailsCacheStore } from 'store/fanDetailsCacheStore'
import { useProfileCacheStore } from 'store/profileCacheStore'

import style from './_user_details.module.scss'
import UserDetailsList from './components/UserDetailsList'
import AddUserToGroups from '../AddUserToGroups/AddUserToGroups'
import BlockUser from '../BlockUser/BlockUser'

export interface UserNote {
  id: number
  created_at: Date
  text: string
}

type ActiveDetails = 'summary' | 'earnings' | 'subscription'

type FanDetailsResponse = {
  conversation_id: string | null
  blocked: boolean
  summary: {
    last_active: string | null
    upsell_conversion_ratio: number
    total_purchases: number
    total_likes: number
    messages_unlock_ratio: number
    unread_messages: number
  }
  earnings: {
    subscriptions: string
    posts: string
    messages: string
    tips: string
    total: string
  }
  subscription: {
    status: string | null
    expiration_date: string | null
    total_month_subscribed: number | null
    subscription_price: string | null
  }
}

const UserDetails: FC<{ user?: any; placeholderSrc?: string }> = ({
  user,
  placeholderSrc
}) => {
  const [activeDetails, setActiveDetails] = useState<ActiveDetails>('summary')
  const [notesOpen, setNotesOpen] = useState(false)
  const [blockOpen, setBlockOpen] = useState(false)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const modalData = useModalContext()
  const queryClient = useQueryClient()

  const fallbackUser = useUserProfile()
  const cachedUserData = useProfileCacheStore(
    (state) => state.cache?.[fallbackUser?.username ?? '']
  )
  const userData = cachedUserData || fallbackUser

  const { getFanDetailsById, setFanDetailsCache } = useFanDetailsCacheStore()

  const userId = user?.id
  const fanDetails = getFanDetailsById(userId) as FanDetailsResponse | undefined

  useQuery(
    ['fanDetails', userId],
    async () => {
      if (!userId) throw new Error('No user ID')
      return await getFanDetails(userId)
    },
    {
      enabled: !!userId,
      onSuccess: (data) => {
        setFanDetailsCache(userId, data)
      },
      staleTime: 30_000
    }
  )
  const addFanToGroupsMutation = useMutation(
    async (info: { id: string; groupIds: string[] }) =>
      await addFanToGroups(info.id, info.groupIds),
    {
      onSuccess: (_resp, params) => {
        queryClient.invalidateQueries('fans-groups')
        queryClient.invalidateQueries([['fan-active-groups', params.id]])
        modalData.clearModal()
      },
      onError: () => {
        addToast('error', t('anErrorOccuredPleaseTryAgain'))
        queryClient.invalidateQueries('fans-groups')
        modalData.clearModal()
      }
    }
  )

  const handlePostReport = () => {
    setBlockOpen(true)
  }

  const handleCloseBlockModal = () => {
    setBlockOpen(false)
  }

const handleOpenChat = () => {
  if (!userId || !fanDetails || fanDetails.blocked) return

  const isSubscribed =
    fanDetails.subscription?.status?.toLowerCase() === 'active'

  navigate(getLink.chatWithUser(userId), {
    state: {
      otherUserData: {
        ...user,
        isSubscribed
      },
      conversationId: fanDetails.conversation_id
    }
  })

  modalData.clearModal()
}
  const resolvedAvatar = user?.avatar?.url || placeholderSrc || user_placeholder
  const resolvedDisplayName = user?.display_name
  const resolvedUsername = user?.username

  const detailsForList = useMemo(() => {
    if (!fanDetails) {
      return {
        summary: {
          last_active: null,
          upsell_conversion_ratio: 0,
          total_purchases: 0,
          total_likes: 0,
          messages_unlock_ratio: 0,
          unread_messages: 0
        },
        earnings: {
          subscriptions: '0.00',
          posts: '0.00',
          messages: '0.00',
          tips: '0.00',
          total: '0.00'
        },
        subscription: {
          status: null,
          expiration_date: null,
          total_month_subscribed: null,
          subscription_price: null
        }
      }
    }

    return fanDetails
  }, [fanDetails])

  return (
    <>
      <div className={style.container}>
        <div className={style.profile_info_container}>
          <div className={style.profile_info_background}>
            <spriteIcons.IconWaveBackground />
          </div>

          <ImgInCircle
            type="profile"
            hasLoader={true}
            hasCamera={false}
            customClass={style.profile_picture}
          >
            <div className={style.profile_holder}>
<img
  src={resolvedAvatar}
  alt="fan"
  className={
    resolvedAvatar === user_placeholder
      ? style.profile_img_placeholder
      : style.profile_img
  }
/>
            </div>
          </ImgInCircle>

          <div className={style.username_container}>
            <div className={style.name}>{resolvedDisplayName}</div>
            <div className={style.username}>@{resolvedUsername}</div>
          </div>

          <div className={style.buttons_container}>
            <div className={style.button_wrapper}>
              <div
                className={style.button}
                onClick={() => {
                  navigate(getLink.userActivity(userId || ''))
                  modalData.clearModal()
                }}
              >
                <div className={style.button_background} />
                <spriteIcons.IconClockArrowCircle />
              </div>
              <div className={style.button_text}>{t('activity')}</div>
            </div>

            <div
              className={`${style.button_wrapper} ${
                fanDetails?.blocked ? style.disabled : ''
              }`}
            >
              <div
                className={style.button}
                onClick={() => {
                  if (!userId || fanDetails?.blocked) return

                  modalData.addModal(
                    t('addUserToGroups'),
                    <AddUserToGroups
                      applyFn={(val: any) =>
                        addFanToGroupsMutation.mutate({ id: userId, groupIds: val })
                      }
                      id={userId}
                    />
                  )
                }}
              >
                <div className={style.button_background}></div>
                <spriteIcons.IconTwoPeopleOutline />
              </div>

              <div className={style.button_text}>{t('groups')}</div>
            </div>


          <div className={style.button_wrapper}>
            <div
              className={style.button}
              onClick={handlePostReport}
            >
              <div className={style.button_background}></div>

              {fanDetails?.blocked ? (
                <spriteIcons.IconBlockedOutline />
              ) : (
                <spriteIcons.IconBlockOutline />
              )}
            </div>

            <div className={style.button_text}>
              {fanDetails?.blocked ? t('Unblock') : t('block')}
            </div>
          </div>

            <div className={style.button_wrapper}>
              <div
                className={`${style.button} ${notesOpen ? style.active : ''}`}
                onClick={() => setNotesOpen(!notesOpen)}
              >
                <div className={style.button_background}></div>
                <spriteIcons.IconNoteOutlineSquare />
              </div>
              <div className={style.button_text}>{t('notes')}</div>
            </div>

            <div
              className={`${style.button_wrapper} ${
                fanDetails?.blocked ? style.disabled : ''
              }`}
            >
              <div
                className={style.button}
                onClick={fanDetails?.blocked ? undefined : handleOpenChat}
              >
                <div className={style.button_background}></div>
                <spriteIcons.IconChatOutlineTransparentBg
                  color={fanDetails?.blocked ? '#B0B0B0' : '#777788'}
                />
              </div>
              <div className={style.button_text}>{t('chat')}</div>
            </div>
          </div>
        </div>

        {!notesOpen && (
          <div className={style.details_container}>
            <div className={style.details_options}>
              {(['summary', 'earnings', 'subscription'] as const).map((section) => (
                <div
                  key={section}
                  className={`${style.details_option} ${
                    activeDetails === section ? style.details_option_active : ''
                  }`}
                  onClick={() => setActiveDetails(section)}
                >
                  {t(section)}
                </div>
              ))}
            </div>

            <UserDetailsList activeDetails={activeDetails} details={detailsForList} />
          </div>
        )}

          {!!notesOpen && userId && (
            <UserNotes
              user_id={userId}
              userPublicId={userId}
            />
          )}
      </div>

      {blockOpen &&
        createPortal(
          <div className={style.block_portal_overlay} onClick={handleCloseBlockModal}>
            <div
              className={style.block_portal_content}
              onClick={(e) => e.stopPropagation()}
            >
              <BlockUser
                user={user}
                onClose={handleCloseBlockModal}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default UserDetails